import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useCallback } from 'react';

import { Server } from '../../apollo/types';
import { useGetAuthedAtTimestamp } from '../auth/useGetAuthedAtTimestamp';

export const GET_PATIENT_REFLECTION_TOPICS_COUNT_QUERY = gql`
  query GetPatientReflectionTopicsCount($patientShortId: ID!) {
    patient(patientShortId: $patientShortId) {
      shortId
      reflectionTopics {
        totalCount
      }
    }
  }
`;

type ReflectionTopicCountQueryResult = {
  patient: {
    reflectionTopics: {
      totalCount: number;
    };
  };
};

export const useGetReflectionTopicCountQuery = (patientShortId: string) => {
  const [authedAtTimestamp] = useGetAuthedAtTimestamp();
  const apolloClient = useApolloClient();
  const { data, loading, error } = useQuery(GET_PATIENT_REFLECTION_TOPICS_COUNT_QUERY, {
    variables: {
      patientShortId: patientShortId,
    },
    context: {
      server: Server.MONOLITH,
    },
    skip: !authedAtTimestamp,
  });

  // Memoized callback to read the cached topic count from the Apollo cache at
  // run time. Returns the count or undefined if no data is available in the
  // Apollo cache
  const getReflectionTopicCount = useCallback(() => {
    if (!patientShortId) return undefined;
    const result = apolloClient.readQuery<ReflectionTopicCountQueryResult>({
      query: GET_PATIENT_REFLECTION_TOPICS_COUNT_QUERY,
      variables: { patientShortId },
    });
    if (result == null) return undefined;
    return result.patient?.reflectionTopics?.totalCount ?? 0;
  }, [apolloClient, patientShortId]);

  return { data, loading, error, getReflectionTopicCount };
};
